import { Controller } from 'stimulus';

/**
 * Expose a print action that dispatches events either side of it, useful
 * for forcing a theme during printing
 */
export default class extends Controller {
  static targets = ['printable'];

  connect() {
    this.printCleanup = null;
    this.printMediaQueryList = null;
    this.handlePrintMediaChange = null;
    this.printingAncestors = [];
    this.currentPrintableTarget = null;
  }

  disconnect() {
    this.clearPendingPrintCleanup();
    this.clearPrintableTree();
  }

  print() {
    this.startPrint();
  }

  printElement() {
    if (!this.hasPrintableTarget) return;

    this.startPrint({
      beforePrint: () => this.markPrintableTree(),
      afterPrint: () => this.clearPrintableTree(),
    });
  }

  startPrint({ beforePrint = null, afterPrint = null } = {}) {
    this.dispatch('print_start');
    beforePrint?.();
    this.registerPrintCleanup(() => {
      afterPrint?.();
      this.dispatch('print_end');
    });

    try {
      window.print();
    } catch (error) {
      this.clearPendingPrintCleanup();
      afterPrint?.();
      this.dispatch('print_end');
      throw error;
    }
  }

  registerPrintCleanup(cleanupCallback) {
    this.clearPendingPrintCleanup();

    const cleanup = () => {
      if (this.printCleanup !== cleanup) return;

      window.removeEventListener('afterprint', cleanup);
      this.removePrintMediaListener();
      this.printCleanup = null;
      cleanupCallback();
    };

    this.printCleanup = cleanup;
    window.addEventListener('afterprint', cleanup, { once: true });

    if (!window.matchMedia) return;

    this.printMediaQueryList = window.matchMedia('print');
    this.handlePrintMediaChange = (event) => {
      if (!event.matches) cleanup();
    };

    if (this.printMediaQueryList.addEventListener) {
      this.printMediaQueryList.addEventListener(
        'change',
        this.handlePrintMediaChange
      );
    } else if (this.printMediaQueryList.addListener) {
      this.printMediaQueryList.addListener(this.handlePrintMediaChange);
    }
  }

  clearPendingPrintCleanup() {
    if (!this.printCleanup) return;

    window.removeEventListener('afterprint', this.printCleanup);
    this.removePrintMediaListener();
    this.printCleanup = null;
  }

  removePrintMediaListener() {
    if (!this.printMediaQueryList || !this.handlePrintMediaChange) return;

    if (this.printMediaQueryList.removeEventListener) {
      this.printMediaQueryList.removeEventListener(
        'change',
        this.handlePrintMediaChange
      );
    } else if (this.printMediaQueryList.removeListener) {
      this.printMediaQueryList.removeListener(this.handlePrintMediaChange);
    }

    this.printMediaQueryList = null;
    this.handlePrintMediaChange = null;
  }

  markPrintableTree() {
    const ancestors = [];

    this.currentPrintableTarget = this.printableTarget;
    let element = this.currentPrintableTarget.parentElement;
    while (element && element !== document.body) {
      element.setAttribute('data-printing-ancestor', '');
      ancestors.push(element);
      element = element.parentElement;
    }

    this.currentPrintableTarget.setAttribute('data-printing', 'target');
    document.body.setAttribute('data-printing', 'page');
    this.printingAncestors = ancestors;
  }

  clearPrintableTree() {
    document.body.removeAttribute('data-printing');
    this.currentPrintableTarget?.removeAttribute('data-printing');
    this.printingAncestors.forEach((element) =>
      element.removeAttribute('data-printing-ancestor')
    );

    this.currentPrintableTarget = null;
    this.printingAncestors = [];
  }
}
